import { BarChart3, Users, Zap } from "lucide-react";

export type LandingBenefit = {
  kicker: string;
  title: string;
  body: string;
  bullets: string[];
  preview: "runway" | "funding-calculator" | "investor-business-plan";
};

export type LandingStep = {
  step: string;
  title: string;
  body: string;
};

export type LandingExample = {
  title: string;
  subtitle: string;
  scenario: string;
  bullets: string[];
  outcome: string;
  accent: string;
};

export type LandingFaq = {
  question: string;
  answer: string;
};

export const LANDING_VALUE_PROPS = [
  { label: "One financial model", detail: "Every chart shares the same engine" },
  { label: "Month-by-month clarity", detail: "Scrub the timeline like a video" },
  { label: "Investor-ready output", detail: "Share links & business plans" },
  { label: "Free to start", detail: "No credit card required" },
] as const;

export const LANDING_STEPS: LandingStep[] = [
  {
    step: "01",
    title: "Set your timeline & cash",
    body: "Pick your startup period, currency, and opening balance. Your runway clock starts from real numbers.",
  },
  {
    step: "02",
    title: "Model revenue & customers",
    body: "Add subscription tiers, per-visit pricing, growth curves, and capacity limits — revenue updates automatically.",
  },
  {
    step: "03",
    title: "Layer expenses & events",
    body: "Salaries, rent, software, funding rounds, and milestones all flow into one cash timeline.",
  },
  {
    step: "04",
    title: "Share with investors",
    body: "Generate a business plan, create PIN-protected share links, and answer AI-grounded questions from your data.",
  },
];

export const LANDING_SPREADSHEET_ROWS = [
  "Timeline you can scrub",
  "Customer-driven revenue",
  "Investor share links",
  "AI grounded in your data",
  "Business plan from live model",
] as const;

export const LANDING_BENEFITS: LandingBenefit[] = [
  {
    kicker: "Runway clarity",
    title: "Know how many months you have left — before it's a crisis",
    body: "Stop updating a runway cell that nobody trusts. Scrub month-by-month across cash, burn, and milestones on one live timeline.",
    bullets: [
      "See runway change when you move a hire or funding round",
      "Spot cash-zero months before you're in the meeting",
      "One number for you, your co-founder, and your deck",
    ],
    preview: "runway",
  },
  {
    kicker: "Size your raise",
    title: "Know how much to ask for — from live burn, not gut feel",
    body: "Flat MRR guesses in a spreadsheet don't survive your first pricing change. Model subscriptions, expenses, and funding events in one engine — then size your opening raise in seconds.",
    bullets: [
      "Funding ASK calculator with Survival, Growth, and Series Prep scenarios",
      "Funding rounds and grants land on the dates they actually close",
      "Every chart updates when you change an assumption",
    ],
    preview: "funding-calculator",
  },
  {
    kicker: "Investor-ready",
    title: "Share one live model — not another exported tab",
    body: "When investors ask follow-up questions, you shouldn't send a third version of the spreadsheet. Give them a read-only link with live charts and a business plan grounded in your burn model.",
    bullets: [
      "PIN-protected share links with live charts",
      "Comments without edit access to your data",
      "AI-generated business plan with MRR, customers, and cost breakdown",
    ],
    preview: "investor-business-plan",
  },
];

export const LANDING_EXAMPLES: LandingExample[] = [
  {
    title: "B2B SaaS · Seed stage",
    subtitle: "Recurring revenue + hiring plan",
    scenario:
      "You're at $8K MRR with two founders, planning to hire sales and raise a $1.5M seed in Q3.",
    bullets: [
      "Model MRR growth from 40 → 200 customers over 18 months",
      "Layer salaries, cloud costs, and 15% monthly sales commission",
      "Drop in a seed round event and see runway extend to 22 months",
      "Export a business plan with projected MRR for investor meetings",
    ],
    outcome: "Know exactly when you need to close the round — and how much.",
    accent: "from-brand/20 to-focus/10",
  },
  {
    title: "Healthcare services · Bootstrapped",
    subtitle: "Per-visit revenue + capacity",
    scenario:
      "A clinic with 3 providers, visit-based billing, and a hard cap on daily appointments.",
    bullets: [
      "Set per-visit revenue plans tied to customer projections",
      "Add a capacity group so growth stops at physical limits",
      "Track payroll, rent, and supplies as fixed monthly burn",
      "Share a read-only investor link when exploring a partnership",
    ],
    outcome: "See break-even month and whether a second location is feasible.",
    accent: "from-revenue/15 to-brand/10",
  },
  {
    title: "Deep tech · Pre-revenue",
    subtitle: "Burn-first, milestone-driven",
    scenario:
      "Small team building hardware, 14 months of runway, targeting a SBIR grant and Series A milestones.",
    bullets: [
      "Map engineering salaries and prototype costs as monthly burn",
      "Add grant and angel events on specific dates",
      "Mark technical milestones on the timeline for narrative context",
      "Use the funding calculator to stress-test a down-round scenario",
    ],
    outcome: "Walk into investor conversations with a defensible cash story.",
    accent: "from-warn/10 to-expense/10",
  },
];

export const LANDING_FAQS: LandingFaq[] = [
  {
    question: "What is a startup runway calculator?",
    answer:
      "A runway calculator estimates how many months your company can operate before cash runs out, based on burn rate and revenue. BurnChart turns that into an interactive burn chart you can scrub month-by-month and share with investors.",
  },
  {
    question: "Is BurnChart a replacement for my accounting software?",
    answer:
      "No — it's a forward-looking planning tool. You model assumptions and scenarios; your bookkeeper still owns historical books. BurnChart shines for runway, fundraising, and hiring decisions.",
  },
  {
    question: "How long does setup take?",
    answer:
      "Most founders have a useful first model in under an hour: timeline, opening cash, a few expenses, and one revenue or customer projection. You can refine over time.",
  },
  {
    question: "Can investors edit my model?",
    answer:
      "Investor links are read-only and PIN-protected. They see live charts and your business plan, and can leave comments — but they cannot change your data.",
  },
  {
    question: "What makes the AI advisor different?",
    answer:
      "The AI advisor and business plan generator read your actual burn chart data — expenses, projections, runway — not generic startup advice. Pro unlocks AI advisor, business plan generation, multiple startups, and API access.",
  },
  {
    question: "How much does BurnChart Pro cost?",
    answer:
      "Your first startup, investor portal, and live burn chart are free. BurnChart Pro is $9.99/month or $99/year and adds multiple startups, AI advisor, AI business plan generation, and API keys.",
  },
];

export const LANDING_PRICING = {
  free: {
    name: "Free",
    price: "$0",
    period: "forever",
    summary: "One startup, full burn chart modeling, investor links",
    bullets: [
      "Interactive runway & burn chart",
      "Expenses, revenue & customer projections",
      "Investor share links & comments",
      "Funding calculator & timeline",
    ],
  },
  pro: {
    name: "Pro",
    monthlyUsd: 9.99,
    yearlyUsd: 99,
    summary: "Scale your portfolio and unlock AI + API",
    bullets: [
      "Multiple startups on one account",
      "AI financial advisor (grounded in your data)",
      "AI business plan generation & PDF export",
      "API keys for integrations",
    ],
  },
} as const;

export const LANDING_AUDIENCE = [
  {
    title: "First-time founders",
    body: "Stop guessing runway in a spreadsheet tab nobody trusts.",
    icon: Zap,
  },
  {
    title: "Fundraising teams",
    body: "One source of truth for deck numbers, data room, and investor Q&A.",
    icon: BarChart3,
  },
  {
    title: "Operators & finance",
    body: "Scenario-plan hires, pricing changes, and expansion before you commit.",
    icon: Users,
  },
] as const;
